<template>
  <div>
    <Button
      v-tooltip="'Open social accounts filter'"
      variant="text"
      color="secondary"
      size="sm"
      radius="lg"
      class="cstu-border cstu-border-gray-400/20"
      :class="{
        '!h-[36px] !font-normal !cstu-text-gray-500 hover:cstu-bg-gray-100/50 hover:cstu-border-gray-400/25': !hasAccountsSelected,
        '!h-[36px] !font-medium !text-white cstu-border cstu-border-gray-400/20': hasAccountsSelected && selectedAccountsForDisplay.length > 0,
      }"
      @click="toggleDrawer"
    >
      <!-- Show selected accounts inside button when accounts are selected -->
      <div v-if="hasAccountsSelected && selectedAccountsForDisplay.length > 0" class="inline-flex items-center space-x-1">
        <div
          v-for="account in selectedAccountsForDisplay.slice(0, 3)"
          :key="getAccountId(account)"
          class="relative"
        >
          <div class="w-7 h-7 rounded-full overflow-hidden bg-gray-100">
            <img
              :src="getAccountImage(account)"
              :alt="getAccountName(account)"
              class="w-full h-full object-cover"
              @error="$event.target.src = 'https://storage.googleapis.com/lumotive-web-storage/default/profile_default.svg'"
            />
          </div>
          <img
            :src="getSocialImage(account.channel_type || account.integration)"
            :alt="account.channel_type || account.integration"
            class="absolute -bottom-0.5 -right-0.5 w-4 h-4"
          />
        </div>
        <!-- Show +X when more than 3 accounts selected -->
        <div v-if="selectedAccountsForDisplay.length > 3" class="bg-gray-300 text-xs text-gray-800 ml-1 p-1 rounded-full">
          +{{ selectedAccountsForDisplay.length - 3 }}
        </div>
      </div>

      <!-- Show "Social Accounts" text when no accounts selected or only noAccount is selected -->
      <span v-else class="flex items-center ml-1">
        <i class="icon-social-accounts-cs mr-1"></i>
        Social Accounts
      </span>
    </Button>

    <!-- Social Accounts Drawer -->
    <CstDrawer
      :model-value="showDrawer"
      title="Social Accounts"
      position="left"
      body-class="!overflow-visible"
      :width="320"
      :no-close-on-backdrop="false"
      @update:model-value="showDrawer = $event"
    >
      <template v-slot:header="{ close }">
        <div class="h-[4.25rem] bottom-border-only flex items-center justify-between py-[16px] px-[14px]">
          <div class="flex items-center gap-[6px]">
            <span class="font-medium text-base">Accounts Filter</span>
          </div>
          <div class="cursor-pointer" @click="close">
            <svg xmlns="http://www.w3.org/2000/svg" width="19" height="19" viewBox="0 0 19 19" fill="none">
              <path d="M10.5605 9.5L13.7803 6.28027C14.0732 5.9873 14.0732 5.5127 13.7803 5.21973C13.4873 4.92676 13.0127 4.92676 12.7197 5.21973L9.5 8.43945L6.28027 5.21973C5.9873 4.92676 5.5127 4.92676 5.21973 5.21973C4.92676 5.5127 4.92676 5.9873 5.21973 6.28027L8.43945 9.5L5.21973 12.7197C4.92676 13.0127 4.92676 13.4873 5.21973 13.7803C5.36621 13.9268 5.55811 14 5.75 14C5.94189 14 6.13379 13.9268 6.28027 13.7803L9.5 10.5605L12.7197 13.7803C12.8662 13.9268 13.0581 14 13.25 14C13.4419 14 13.6338 13.9268 13.7803 13.7803C14.0732 13.4873 14.0732 13.0127 13.7803 12.7197L10.5605 9.5Z" fill="#4A4A4A"/>
            </svg>
          </div>
        </div>
      </template>

      <template v-slot>
        <div class="px-2 py-[11px] h-full flex flex-col">
          <!-- Search Input -->
          <div class="px-3 mb-4">
            <div class="flex items-center bg-gray-100/70 rounded-md px-3 py-[5px]">
              <img
                src="@src/assets/img/chat_bot/search-icon.svg"
                class="h-4 w-4"
                alt="search"
              />
              <input
                v-model="searchQuery"
                type="text"
                class="bg-transparent ml-2 text-sm border-0 p-1 rounded flex-1 outline-none"
                placeholder="Search accounts by name"
              />
              <span v-if="searchQuery" class="cursor-pointer ml-1" @click.stop="clearSearch">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path d="M8.4988 8L11.7186 4.78027C12.0115 4.4873 12.0115 4.0127 11.7186 3.71973C11.4256 3.42676 10.951 3.42676 10.658 3.71973L7.4383 6.93945L4.21857 3.71973C3.9256 3.42676 3.451 3.42676 3.15803 3.71973C2.86506 4.0127 2.86506 4.4873 3.15803 4.78027L6.37775 8L3.15803 11.2197C2.86506 11.5127 2.86506 11.9873 3.15803 12.2803C3.30451 12.4268 3.49641 12.5 3.6883 12.5C3.88019 12.5 4.07209 12.4268 4.21857 12.2803L7.4383 9.06055L10.658 12.2803C10.8045 12.4268 10.9964 12.5 11.1883 12.5C11.3802 12.5 11.5721 12.4268 11.7186 12.2803C12.0115 11.9873 12.0115 11.5127 11.7186 11.2197L8.4988 8Z" fill="#6B7280"/>
                </svg>
              </span>
            </div>
          </div>

          <!-- Group By Options -->
          <div class="flex items-center justify-between px-3 mb-4">
            <div class="flex items-center gap-3">
              <span class="text-[#75797C] text-sm font-normal">{{ selectedGroupFilter.filterTitle }}</span>
              <Dropdown placement="bottom" dropdown-classes="!min-w-[200px] !z-[1024]" button-classes="border-none hover:!border-none !px-0 !gap-0">
                <template v-slot:selected>
                  <span v-tooltip="'Group By'" class="cursor-pointer" >
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                      <g clip-path="url(#clip0_34264_15073)">
                        <path d="M5.61639 2.57959H1.52555C1.14899 2.57959 0.84375 2.88483 0.84375 3.26139V7.35223C0.84375 7.72879 1.14899 8.03404 1.52555 8.03404H5.61639C5.99295 8.03404 6.2982 7.72879 6.2982 7.35223V3.26139C6.2982 2.88483 5.99295 2.57959 5.61639 2.57959ZM4.93459 6.67043H2.20735V3.94319H4.93459V6.67043Z" fill="#4A4A4A"/>
                        <path d="M15.162 4.625H8.34391C7.96735 4.625 7.66211 4.93024 7.66211 5.3068C7.66211 5.68336 7.96735 5.9886 8.34391 5.9886H15.162C15.5386 5.9886 15.8438 5.68336 15.8438 5.3068C15.8438 4.93027 15.5385 4.625 15.162 4.625Z" fill="#4A4A4A"/>
                        <path d="M15.162 11.4431H8.34391C7.96735 11.4431 7.66211 11.7484 7.66211 12.1249C7.66211 12.5014 7.96735 12.8067 8.34391 12.8067H15.162C15.5386 12.8067 15.8438 12.5014 15.8438 12.1249C15.8438 11.7484 15.5385 11.4431 15.162 11.4431Z" fill="#4A4A4A"/>
                        <path d="M5.13937 10.2765L2.86964 12.519L2.01602 11.6327C1.75413 11.3619 1.32235 11.3548 1.0516 11.6166C0.780853 11.8785 0.773677 12.3103 1.03557 12.5811L2.36781 13.9658C2.49485 14.0954 2.66835 14.169 2.84986 14.1703H2.85804C3.03679 14.1698 3.2082 14.099 3.3353 13.9733L6.0939 11.2461C6.3793 11.0005 6.41155 10.5699 6.16593 10.2845C5.92031 9.99915 5.4898 9.9669 5.2044 10.2125C5.18134 10.2324 5.1596 10.2538 5.13937 10.2765Z" fill="#4A4A4A"/>
                      </g>
                      <defs>
                        <clipPath id="clip0_34264_15073">
                          <rect width="15" height="15" fill="white" transform="translate(0.84375 0.875)"/>
                        </clipPath>
                      </defs>
                    </svg>
                  </span>
                </template>
                <template v-slot:arrow>
                  <span></span>
                </template>
                <span class="text-[#75797C] text-sm font-normal">Group By</span>
                <hr class="my-0" />
                <div class="flex flex-col gap-2">
                  <template v-for="option in groupFilterOptions" :key="option.value">
                    <DropdownItem
                      class="flex items-center justify-between"
                      size="sm"
                      :selected="selectedGroupFilter.value === option.value"
                      @click="selectedGroupFilter = option"
                    >
                      {{ option.label }}
                      <i v-if="selectedGroupFilter.value === option.value" class="far fa-check" />
                    </DropdownItem>
                  </template>
                </div>
              </Dropdown>
            </div>
            <!-- Hide Select All checkbox in grid view since only single selection is allowed -->
            <div v-if="!isGridView" class="flex items-center gap-1 mx-1">
               <!-- Collapse/Expand All Platforms -->
               <Button
                v-if="selectedGroupFilter.value === 'with'"
                v-tooltip="`${allPlatformsCollapsed ? 'Expand all platforms' : 'Collapse all platforms'}`"
                variant="text"
                color="light"
                size="sm"
                @click="toggleAllPlatformsCollapse"
             >
               <i :class="`text-gray-800 fas fa-chevron-${allPlatformsCollapsed ? 'up' : 'down'} mt-1`"></i>
             </Button>

              <Checkbox
                v-tooltip="`${isAllSelected ? 'Unselect All' : 'Select All'}`"
                class="mr-3.5"
                :model-value="isAllSelected"
                :indeterminate="isIndeterminate"
                @update:model-value="value => value ? selectAllAccounts() : deselectAllAccounts()"
              />

            </div>
          </div>

          <!-- Accounts List -->
          <div class="flex-1 flex flex-col gap-1 overflow-auto scrollbar-gutter">
            <!-- No Account Option -->
            <template v-if="!isGridView">
              <ListItem
                class="!min-h-[50px] flex items-center gap-[14px] group select-none hover:font-medium !py-[12px] mx-1"
                :class="{
                  '!font-medium': localNoAccount,
                }"
                @click="toggleNoAccount"
              >
                <div class="relative">
                  <span
                    class="bg-gray-500 rounded-full block relative"
                    style="width: 30px; height: 30px"
                  >
                    <span
                      class="
                        rounded-full
                        w-4
                        h-4
                        absolute
                        -right-0.5
                        -bottom-0.5
                        flex
                        justify-center
                        items-center
                        bg-white
                      "
                    >
                      <svg
                        fill="none"
                        height="14"
                        viewBox="0 0 304 304"
                        width="14"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M152 303.55C199.117 303.527 243.539 281.593 272.2 244.202C300.864 206.807 310.505 158.21 298.29 112.702C286.071 67.2021 253.388 29.9681 209.849 11.9621C166.31 -6.04593 116.876 -2.77994 76.079 20.8019C35.29 44.3799 7.78603 85.5868 1.66103 132.302C-4.46007 179.017 11.4891 225.923 44.821 259.222C73.227 287.667 111.798 303.62 151.991 303.55L152 303.55ZM232.512 232.515C213.508 251.472 188.41 263.097 161.664 265.324C134.914 267.551 108.238 260.238 86.363 244.683L244.583 86.2231C260.188 108.098 267.54 134.797 265.329 161.578C263.118 188.355 251.489 213.488 232.509 232.512L232.512 232.515ZM71.492 71.4951C90.492 52.5381 115.59 40.9131 142.34 38.6861C169.09 36.4595 195.766 43.7681 217.641 59.3231L59.421 217.833C43.8 195.954 36.444 169.247 38.655 142.454C40.8659 115.661 52.503 90.5201 71.495 71.4971L71.492 71.4951Z"
                          fill="#FF001B"
                        />
                      </svg>
                    </span>
                  </span>
                </div>
                <p class="flex-1 text-sm truncate text-[#3A4557]">
                  No Accounts
                </p>
                <div class="flex items-center gap-2" @click.stop>
                  <button
                    v-if="!localNoAccount"
                    class="text-xs font-normal border rounded-md bg-gray-400 hover:bg-gray-500 px-1 hidden group-hover:inline-flex"
                    @click.stop="handleOnlyFilter('noAccount')"
                  >
                    only
                  </button>
                  <Checkbox
                    :model-value="localNoAccount"
                    @update:model-value="toggleNoAccount"
                  />
                </div>
              </ListItem>
            </template>

            <!-- No Results Message -->
            <div v-if="searchQuery.trim() && hasNoSearchResults" class="flex flex-col items-center justify-center py-8 px-4 text-center">
              <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" class="text-gray-400 mb-3">
                <circle cx="11" cy="11" r="8"/>
                <path d="m21 21-4.35-4.35"/>
              </svg>
              <h3 class="text-sm font-medium text-gray-900 mb-1">No accounts found</h3>
              <p class="text-xs text-gray-500">Try adjusting your search terms</p>
            </div>

            <!-- Grouped by Platform -->
            <template v-if="selectedGroupFilter.value === 'with' && !hasNoSearchResults">
              <template v-for="platform in Object.keys(filteredGroupedAccounts)" :key="platform">
                <Collapsible
                  v-if="filteredGroupedAccounts[platform].length > 0"
                  :model-value="!collapsedPlatforms[platform]"
                  :hide-arrow="true"
                  button-class="!h-[40px] cstu-bg-gray-100 active:cstu-bg-gray-100 !py-[8px] !mx-1 !mb-2 !rounded-lg"
                  selected-class="cstu-bg-gray-100 active:cstu-bg-gray-100"
                >
                  <template v-slot:trigger="{ open }">
                    <div class="flex items-center gap-[11px] w-full select-none" @click.stop="togglePlatformCollapse(platform)">
                      <!-- Collapse/Expand Icon -->
                      <svg
                        :class="{ 'rotate-180': open }"
                        class="w-4 h-4 transition-transform duration-200 text-gray-700 cursor-pointer"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"></path>
                      </svg>

                      <img
                        :src="getSocialIcon(platform)"
                        class="w-5 h-5"
                        :alt="platform"
                      />
                      <div class="flex-1 truncate">
                        <span class="text-[#3A4557] font-medium truncate capitalize">{{ platform }}</span>
                      </div>
                      <div
                        v-if="filteredGroupedAccounts[platform].length > 0"
                        v-tooltip="`${filteredGroupedAccounts[platform].length}`"
                        class="text-[#3A4557] text-xs leading-none font-medium flex min-w-[35px] px-[10px] py-1 justify-center items-center gap-[10px] flex-shrink-0 rounded-[20px] bg-[#E8E8E8]"
                      >
                        {{ filteredGroupedAccounts[platform].length }}
                      </div>
                      <!-- Hide platform checkbox in grid view since only single selection is allowed -->
                      <div v-if="!isGridView" @click.stop>
                        <Checkbox
                          :model-value="isPlatformAllSelected(platform)"
                          :indeterminate="isPlatformIndeterminate(platform)"
                          @update:model-value="value => value ? selectPlatformAccounts(platform) : deselectPlatformAccounts(platform)"
                        />
                      </div>

                    </div>
                  </template>

                  <div class="ml-1">
                    <template v-for="account in filteredGroupedAccounts[platform]" :key="getAccountId(account)">
                      <div class="mb-1">
                        <AccountListItem
                          :account="account"
                          :platform="platform"
                          :is-grouped-view="true"
                          :selected="isAccountSelected(account)"
                          @click="toggleAccountSelection(account)"
                          @only="handleOnlyFilter('account', account)"
                        />
                      </div>
                    </template>
                  </div>
                </Collapsible>
              </template>
            </template>

            <!-- Without Platform Grouping -->
            <template v-if="selectedGroupFilter.value === 'without' && !hasNoSearchResults">
              <template v-for="account in filteredVirtualAccountsFlat" :key="account.id">
                <div class="mb-2">
                  <AccountListItem
                    :account="account"
                    :platform="account.platform"
                    :is-grouped-view="false"
                    :selected="isAccountSelected(account)"
                    @click="toggleAccountSelection(account)"
                    @only="handleOnlyFilter('account', account)"
                  />
                </div>
              </template>
            </template>
          </div>

          <!-- Apply Button -->
          <div class="absolute bottom-3 ml-12 flex justify-center gap-2">
            <!-- Reset Button -->
             <Button size="sm" color="light" variant="text" @click="clearSelection ">Reset Filters</Button>
            <Button
              size="sm"
              @click="applySelection"
            >
              Apply
            </Button>
          </div>
        </div>
      </template>
    </CstDrawer>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onUnmounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import CstDrawer from '@ui/Drawer/CstDrawer.vue'
import { ListItem, Button, Checkbox, Dropdown, DropdownItem, Collapsible } from '@contentstudio/ui'
import useSocialGridView from '@src/modules/planner_v2/composables/useSocialGridView'
import { EventBus } from '@common/lib/event-bus'
import AccountListItem from './SocialAccountListItem.vue'
import { useComposerHelper } from '@/src/modules/composer_v2/composables/useComposerHelper'

const { getSocialIcon } = useComposerHelper()
const { selectedAccount: selectedAccountInGridView } = useSocialGridView()
const router = useRouter()

const props = defineProps({
  modelValue: {
    type: Array,
    default: () => []
  },
  noAccount: {
    type: Boolean,
    default: false
  },
  connectedSocialAccountsList: {
    type: Array,
    default: () => []
  },
  isGridView: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['update:modelValue', 'update:noAccount', 'update-selected-accounts'])

const route = useRoute()

// Data
const showDrawer = ref(false)
const selectedAccounts = ref([...props.modelValue])
const localNoAccount = ref(props.noAccount)
const appliedSelectedAccounts = ref([...props.modelValue])
const appliedNoAccount = ref(props.noAccount)
const selectedAccountsMap = ref({})
const searchQuery = ref('')

// Collapse state for platforms
const collapsedPlatforms = ref({})
const allPlatformsCollapsed = ref(false)
const isGlobalCollapseUserAction = ref(false)

const groupFilterOptions = [

  { value: 'without', label: 'Without Platform', filterTitle: 'All Platforms' },
  { value: 'with', label: 'Platform', filterTitle: 'By Platform' }
]

const selectedGroupFilter = ref(groupFilterOptions[0])

// Platform ordering - Facebook first, then alphabetical
const platformOrder = [
  'facebook',
  'instagram',
  'threads',
  'twitter',
  'linkedin',
  'pinterest',
  'gmb',
  'youtube',
  'tiktok',
  'tumblr',
  'bluesky',
  'wordpress',
  'medium',
  'shopify',
  'webflow'
]

const getPlatformOrder = (platform) => {
  const index = platformOrder.indexOf(platform.toLowerCase())
  return index === -1 ? 999 : index
}

const getFilteredSocialAccounts = (accounts, routeName, platformToFilter = undefined, token = 'grid_view') => {
  const SUPPORTED_PLATFORMS = {
    INSTAGRAM: 'instagram',
    TIKTOK: 'tiktok'
  }

  if (!platformToFilter) {
    for (const [key, value] of Object.entries(SUPPORTED_PLATFORMS)) {
      if (routeName.includes(token) && routeName.includes(value)) {
        platformToFilter = value
        break
      }
    }
  }

  if (platformToFilter) {
    return accounts.filter((account) => {
      return account.channel_type === platformToFilter
    })
  }

  return accounts
}

const getAccountId = (account) => {
  if (account.platform_type === 'blog') {
    return account.platform_identifier
  }
  return account[account.channel_identifier]
}

// Initialize selected accounts map for O(1) lookup
const initializeSelectedAccountsMap = () => {
  const map = {}
  selectedAccounts.value.forEach(accountId => {
    map[accountId] = true
  })
  selectedAccountsMap.value = map
}

// Auto-select first account in grid view
const autoSelectFirstAccountInGridView = () => {
  if (props.isGridView && filteredAccounts.value.length > 0) {
    // In grid view, always ensure we have exactly one account selected from the filtered accounts
    // This handles both initial selection and platform switching
    let accountToSelect = null

    // Check if there's an account_id in the route query (for page refresh)
    const queryAccountId = route.query.account_id
    if (queryAccountId) {
      // Find the account that matches the query account_id
      accountToSelect = filteredAccounts.value.find(account => {
        if (route.name.includes('tiktok')) {
          return account.platform_identifier === queryAccountId
        } else {
          return account.instagram_id === queryAccountId
        }
      })
    }
    
    // If no account found from query or no query, select the first account
    if (!accountToSelect) {
      accountToSelect = filteredAccounts.value[0]
    }

    const accountId = getAccountId(accountToSelect)
    
    // Only update if the selection has actually changed
    if (selectedAccounts.value.length !== 1 || selectedAccounts.value[0] !== accountId) {
      selectedAccounts.value = [accountId]
      appliedSelectedAccounts.value = [accountId]
      selectedAccountsMap.value = { [accountId]: true }

      // Update the grid view selected account
      selectedAccountInGridView.value = accountToSelect

      // Emit the selection
      emit('update:modelValue', [accountId])
      emit('update:noAccount', false)
      emit('update-selected-accounts', [accountId], false, true)
    }
  } else if (!props.isGridView && selectedAccounts.value.length > 0) {
    // Clear selection when switching from grid view to non-grid view
    selectedAccounts.value = []
    appliedSelectedAccounts.value = []
    selectedAccountsMap.value = {}
    localNoAccount.value = false
    appliedNoAccount.value = false
    selectedAccountInGridView.value = null

    // Emit the cleared selection
    emit('update:modelValue', [])
    emit('update:noAccount', false)
    emit('update-selected-accounts', [], false, true)
  }
}

// Computed
const hasAccountsSelected = computed(() => {
  return appliedSelectedAccounts.value.length > 0 || appliedNoAccount.value
})

const filteredAccounts = computed(() => {
  if (props.isGridView) {
    return getFilteredSocialAccounts(
      props.connectedSocialAccountsList,
      route.name,
    )
  }
  return props.connectedSocialAccountsList
})

const groupedAccounts = computed(() => {
  const groups = {}
  filteredAccounts.value.forEach(account => {
    const platform = account.channel_type || account.integration || account.platform_type
    if (!groups[platform]) {
      groups[platform] = []
    }
    groups[platform].push(account)
  })

  // Sort platforms according to the defined order
  const sortedGroups = {}
  Object.keys(groups)
    .sort((a, b) => getPlatformOrder(a) - getPlatformOrder(b))
    .forEach(platform => {
      sortedGroups[platform] = groups[platform]
    })

  return sortedGroups
})

const filteredGroupedAccounts = computed(() => {
  if (!searchQuery.value.trim()) {
    return groupedAccounts.value
  }

  const filteredGroups = {}
  const searchTerm = searchQuery.value.toLowerCase().trim()

  Object.keys(groupedAccounts.value).forEach(platform => {
    filteredGroups[platform] = groupedAccounts.value[platform].filter(account => {
      const accountName = (account.platform_name || account.name || '').toLowerCase()
      return accountName.includes(searchTerm)
    })
  })
  return filteredGroups
})

const filteredVirtualAccountsFlat = computed(() => {
  const accounts = []
  Object.entries(filteredGroupedAccounts.value).forEach(([platform, platformAccounts]) => {
    platformAccounts.forEach(account => {
      accounts.push({
        ...account,
        id: getAccountId(account),
        platform
      })
    })
  })
  return accounts
})

const selectedAccountsForDisplay = computed(() => {
  return filteredAccounts.value.filter(account => appliedSelectedAccounts.value.includes(getAccountId(account)))
})

const selectedAccountsCount = computed(() => {
  return selectedAccounts.value.length + (localNoAccount.value ? 1 : 0)
})

const isAllSelected = computed(() => {
  // Get the accounts that are currently visible based on search and grouping
  const visibleAccounts = selectedGroupFilter.value === 'with'
    ? Object.values(filteredGroupedAccounts.value).flat()
    : filteredVirtualAccountsFlat.value

  if (visibleAccounts.length === 0) return false
  const allAccountsSelected = visibleAccounts.every(account => selectedAccountsMap.value[getAccountId(account)])
  return allAccountsSelected && localNoAccount.value
})

const isIndeterminate = computed(() => {
  // Get the accounts that are currently visible based on search and grouping
  const visibleAccounts = selectedGroupFilter.value === 'with'
    ? Object.values(filteredGroupedAccounts.value).flat()
    : filteredVirtualAccountsFlat.value

  if (visibleAccounts.length === 0) return false
  const someAccountsSelected = visibleAccounts.some(account => selectedAccountsMap.value[getAccountId(account)])
  const allAccountsSelected = visibleAccounts.every(account => selectedAccountsMap.value[getAccountId(account)])
  return (someAccountsSelected && !allAccountsSelected) ||
         (someAccountsSelected && !localNoAccount.value) ||
         (!someAccountsSelected && localNoAccount.value)
})

const hasNoSearchResults = computed(() => {
  if (!searchQuery.value.trim()) return false
  return Object.keys(filteredGroupedAccounts.value).length === 0
})

// Watch for changes and update virtual lists
watch(() => props.modelValue, (newVal) => {
  selectedAccounts.value = [...newVal]
  appliedSelectedAccounts.value = [...newVal]
  initializeSelectedAccountsMap()
}, { deep: true })

watch(() => props.noAccount, (newVal) => {
  localNoAccount.value = newVal
  appliedNoAccount.value = newVal
})

watch(filteredAccounts, () => {
  autoSelectFirstAccountInGridView()
})

watch(allPlatformsCollapsed, (newValue) => {
  // Only sync individual platform states when it's a user action (clicking the global button)
  // Don't sync when it's a programmatic reset from individual platform toggle
  if (isGlobalCollapseUserAction.value) {
    Object.keys(groupedAccounts.value).forEach(platform => {
      collapsedPlatforms.value[platform] = newValue
    })
    isGlobalCollapseUserAction.value = false // Reset the flag
  }
})


const clearSearch = () => {
  searchQuery.value = ''
}

onMounted(() => {
  initializeSelectedAccountsMap()
  autoSelectFirstAccountInGridView()

  EventBus.$on('update-selected-accounts', (accounts) => {
    console.log('update-selected-accounts', accounts)
    selectedAccounts.value = [...accounts]
    appliedSelectedAccounts.value = [...accounts]
    emit('update:modelValue', accounts)
    emit('update:noAccount', false)

    // select the accounts as well
    selectedAccountsMap.value = accounts.reduce((acc, account) => {
      acc[account] = true
      return acc
    }, {})

  })
})

onUnmounted(() => {
  EventBus.$off('update-selected-accounts')
})

// Methods
const toggleDrawer = () => {
  showDrawer.value = !showDrawer.value
}

const getAccountImage = (account) => {
  if (account.platform_logo) return account.platform_logo
  if (account.image) return account.image
  if (account.picture?.url) return account.picture.url
  return 'https://storage.googleapis.com/lumotive-web-storage/default/profile_default.svg'
}

const getAccountName = (account) => {
  return account.platform_name || account.name || 'Unknown Account'
}

const isAccountSelected = (account) => {
  const accountId = getAccountId(account)
  return selectedAccountsMap.value[accountId] || false
}

const toggleAccountSelection = (account) => {
  const accountId = getAccountId(account)
  const newMap = { ...selectedAccountsMap.value }

  if (props.isGridView) {
    // Clear all selections first in grid view
    selectedAccounts.value = [accountId]
    selectedAccountsMap.value = { [accountId]: true }
  } else {
    if (newMap[accountId]) {
      delete newMap[accountId]
      const index = selectedAccounts.value.indexOf(accountId)
      if (index > -1) {
        selectedAccounts.value.splice(index, 1)
      }
    } else {
      newMap[accountId] = true
      selectedAccounts.value.push(accountId)
    }
    selectedAccountsMap.value = newMap
  }
}

const toggleNoAccount = () => {
  localNoAccount.value = !localNoAccount.value
}

const selectAllAccounts = () => {
  const newMap = {}
  const newSelectedAccounts = []

  // Use requestIdleCallback for better performance with large lists
  const processAccounts = () => {
    // Use filtered accounts based on search query
    const accountsToSelect = selectedGroupFilter.value === 'with'
      ? Object.values(filteredGroupedAccounts.value).flat()
      : filteredVirtualAccountsFlat.value

    accountsToSelect.forEach(account => {
      const accountId = getAccountId(account)
      newMap[accountId] = true
      newSelectedAccounts.push(accountId)
    })

    selectedAccounts.value = newSelectedAccounts
    selectedAccountsMap.value = newMap
    localNoAccount.value = true
  }

  if (window.requestIdleCallback) {
    window.requestIdleCallback(processAccounts)
  } else {
    setTimeout(processAccounts, 0)
  }
}

const deselectAllAccounts = () => {
  selectedAccounts.value = []
  selectedAccountsMap.value = {}
  localNoAccount.value = false
}

const isPlatformAllSelected = (platform) => {
  const platformAccounts = filteredGroupedAccounts.value[platform] || []
  return platformAccounts.length > 0 && platformAccounts.every(account => selectedAccountsMap.value[getAccountId(account)])
}

const isPlatformIndeterminate = (platform) => {
  const platformAccounts = filteredGroupedAccounts.value[platform] || []
  const selectedCount = platformAccounts.filter(account => selectedAccountsMap.value[getAccountId(account)]).length
  return selectedCount > 0 && selectedCount < platformAccounts.length
}

const selectPlatformAccounts = (platform) => {
  const platformAccounts = filteredGroupedAccounts.value[platform] || []

  if (props.isGridView) {
    // In grid view, select only the first account of the platform
    if (platformAccounts.length > 0) {
      const accountId = getAccountId(platformAccounts[0])
      selectedAccounts.value = [accountId]
      selectedAccountsMap.value = { [accountId]: true }
      selectedAccountInGridView.value = platformAccounts[0]
    }
  } else {
    // In normal view, select all accounts of the platform
    const newSelectedAccounts = [...selectedAccounts.value]
    const newMap = { ...selectedAccountsMap.value }

    platformAccounts.forEach(account => {
      const accountId = getAccountId(account)
      if (!newMap[accountId]) {
        newMap[accountId] = true
        newSelectedAccounts.push(accountId)
      }
    })

    selectedAccounts.value = newSelectedAccounts
    selectedAccountsMap.value = newMap
  }

  // Reset no account selection when selecting accounts
  localNoAccount.value = false
}

const deselectPlatformAccounts = (platform) => {
  const platformAccounts = filteredGroupedAccounts.value[platform] || []
  const newSelectedAccounts = [...selectedAccounts.value]
  const newMap = { ...selectedAccountsMap.value }

  platformAccounts.forEach(account => {
    const accountId = getAccountId(account)
    if (newMap[accountId]) {
      delete newMap[accountId]
      const index = newSelectedAccounts.indexOf(accountId)
      if (index > -1) {
        newSelectedAccounts.splice(index, 1)
      }
    }
  })

  selectedAccounts.value = newSelectedAccounts
  selectedAccountsMap.value = newMap

  // Clear grid view selection if deselecting in grid view
  if (props.isGridView && newSelectedAccounts.length === 0) {
    selectedAccountInGridView.value = null
  }
}

const handleOnlyFilter = (type, data) => {
  if (type === 'noAccount') {
    selectedAccounts.value = []
    selectedAccountsMap.value = {}
    localNoAccount.value = true
  } else if (type === 'account') {
    const accountId = getAccountId(data)
    selectedAccounts.value = [accountId]
    selectedAccountsMap.value = { [accountId]: true }
    localNoAccount.value = false
  }
}

const clearSelection = () => {
  selectedAccounts.value = []
  selectedAccountsMap.value = {}
  localNoAccount.value = false
  applySelection()
}

const applySelection = async () => {
  appliedSelectedAccounts.value = [...selectedAccounts.value]
  appliedNoAccount.value = localNoAccount.value
  emit('update:modelValue', [...appliedSelectedAccounts.value])
  emit('update:noAccount', appliedNoAccount.value)
  emit('update-selected-accounts', [...appliedSelectedAccounts.value], appliedNoAccount.value, true)

  if (props.isGridView) {
    // get the account from selectedAccounts.value based on the account_id
    selectedAccountInGridView.value = filteredAccounts.value?.find(account => appliedSelectedAccounts.value.includes(getAccountId(account)))

    const query = {}
    if (route.name.includes('tiktok')) {
      query.account_id = selectedAccountInGridView.value?.platform_identifier
    } else {
      query.account_id = selectedAccountInGridView.value?.instagram_id
    }
    await router.replace({ query })
  }
}

const togglePlatformCollapse = (platform) => {
  collapsedPlatforms.value[platform] = !collapsedPlatforms.value[platform]

  // If expanding a platform while all platforms are collapsed globally,
  // reset the global collapse state (without triggering the watcher)
  if (!collapsedPlatforms.value[platform] && allPlatformsCollapsed.value) {
    allPlatformsCollapsed.value = false
  }
}

const toggleAllPlatformsCollapse = () => {
  isGlobalCollapseUserAction.value = true
  allPlatformsCollapsed.value = !allPlatformsCollapsed.value
}

const getSocialImage = (channelType) => {
  if (!channelType) return ''
  const blogPlatforms = ['wordpress', 'shopify', 'webflow' , 'medium']
  const type = channelType.toLowerCase()
  // if type is blog, return without rounded
  if (blogPlatforms.includes(type)) {
    return require(`@assets/img/integration/${type}.svg`)
  }
    if (type === 'twitter') {
      return require('@assets/img/integration/twitter-x-rounded.svg')
    } else {
      return require(`@assets/img/integration/${type}-rounded.svg`)
    }
}
</script>

<style>
.scrollbar-gutter {
  scrollbar-gutter: stable;
}

/* Virtual scrolling optimization */
:deep(.vue-recycle-scroller__item-wrapper) {
  overflow: visible !important;
}

:deep(.vue-recycle-scroller__item-view) {
  overflow: visible !important;
}

/* Add will-change to improve rendering performance */
:deep(.vue-recycle-scroller) {
  will-change: transform;
}

/* Optimize transitions for account items */
.account-item {
  transition: background-color 0.15s ease-in-out;
  will-change: background-color;
}
</style>
